import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputOtpModule } from 'primeng/inputotp';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
    selector: 'qc-otp-verification',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        InputOtpModule,
        MessageModule,
        ProgressSpinnerModule,
    ],
    template: `
        <form [formGroup]="otpForm" class="mb-6">
            <div class="mb-4">
                <div *ngIf="phoneNumber" class="opacity-50 mb-5  text-center">
                    Code sent to: <strong>{{ phoneNumber }}</strong>
                </div>
                <div class="flex justify-center">
                    <p-inputOtp
                        formControlName="code"
                        [length]="6"
                        [integerOnly]="true"
                        size="large"
                        (onChange)="onOtpChange($event)"
                    />
                </div>
                <small
                    *ngIf="otpFormSubmitted && otpForm.controls.code.errors?.['required']"
                    class="text-rose-500 text-xs mt-2 block text-center"
                >
                    Verification code is required
                </small>
                <small
                    *ngIf="otpFormSubmitted && otpForm.controls.code.errors?.['pattern']"
                    class="text-rose-500 text-xs mt-2 block text-center"
                >
                    Please enter a valid 6-digit code
                </small>
            </div>
        </form>

        <!-- Resend code section for SMS -->
        <div *ngIf="showResend" class="text-center mb-4">
            <span class="text-sm opacity-70">Didn't receive the code? </span>
            <button
                *ngIf="resendCountdown === 0"
                type="button"
                class="text-sm text-primary-600 hover:text-primary-800 underline cursor-pointer bg-transparent border-none"
                [disabled]="isResending"
                (click)="handleResend()"
            >
                {{ isResending ? 'Sending...' : 'Resend code' }}
            </button>
            <span *ngIf="resendCountdown > 0" class="text-sm opacity-50">
                Resend in {{ resendCountdown }}s
            </span>
        </div>

        <p-message *ngIf="resendSuccess" severity="success" text="New verification code sent!" styleClass="w-full mb-4"></p-message>

        <div *ngIf="isLoading" class="flex justify-center my-4">
            <p-progressSpinner [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>

        <p-message *ngIf="error" severity="error" [text]="error" styleClass="w-full mb-4"></p-message>

        <p-button
            type="button"
            label="Verify"
            icon="pi pi-check"
            size="large"
            styleClass="w-full mt-12"
            [loading]="isLoading"
            [disabled]="otpForm.invalid"
            (click)="onVerify.emit()"
        />
    `,
})
export class OtpVerificationComponent implements OnInit, OnDestroy {
    @Input() otpForm!: FormGroup;
    @Input() otpFormSubmitted: boolean = false;
    @Input() isLoading: boolean = false;
    @Input() error: string = '';
    @Input() phoneNumber: string | null = null;
    @Input() showResend: boolean = false;
    @Input() mfaMethodId: string | null = null;

    @Output() onVerify = new EventEmitter<void>();
    @Output() onResendCode = new EventEmitter<void>();

    resendCountdown: number = 0;
    isResending: boolean = false;
    resendSuccess: boolean = false;
    private countdownInterval: ReturnType<typeof setInterval> | null = null;

    ngOnInit(): void {
        // Start with a 60-second countdown when component loads (code was just sent)
        if (this.showResend) {
            this.startCountdown();
        }
    }

    ngOnDestroy(): void {
        this.clearCountdown();
    }

    onOtpChange(event: any): void {
        const value = event.value;
        if (value && value.length === 6 && /^\d{6}$/.test(value)) {
            this.onVerify.emit();
        }
    }

    handleResend(): void {
        if (this.resendCountdown > 0 || this.isResending) {
            return;
        }
        this.isResending = true;
        this.resendSuccess = false;
        this.onResendCode.emit();
    }

    onResendSuccess(): void {
        this.isResending = false;
        this.resendSuccess = true;
        this.otpForm.get('code')?.reset();
        this.startCountdown();
        // Hide success message after 3 seconds
        setTimeout(() => {
            this.resendSuccess = false;
        }, 3000);
    }

    onResendError(): void {
        this.isResending = false;
    }

    private startCountdown(): void {
        this.clearCountdown();
        this.resendCountdown = 60;
        this.countdownInterval = setInterval(() => {
            this.resendCountdown--;
            if (this.resendCountdown <= 0) {
                this.clearCountdown();
            }
        }, 1000);
    }

    private clearCountdown(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
        this.resendCountdown = 0;
    }
}
